import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import constants from 'app/constants'
import { LOCAL_STORAGE } from 'app/localStorageManager'
import { usePersonaContext } from 'app/services/PersonaProvider/PersonaProvider'
import { useModalContext } from 'components/interface/Modal/provider/ModalProvider'
import useLogger from 'lib/util/useLogger'

const debug = useLogger('Cart', 'teal')

/**
 * The `useCart`
 *
 * @param {object} account - the account detail
 * @returns {object} the context object
 */
function useCart (account) {
  const { isSignedIn } = account
  const { storeData } = usePersonaContext()
  const { showModal } = useModalContext()
  const [isCartReady, setIsCartReady] = useState(false)
  const [cart, setCart] = useState([])
  const [lostSupplyCart, setLostSupplyCart] = useState([])
  const lastCartCount = useRef(0)

  /**
   * Memo: Keep a tally of the cart count
   */
  const cartCount = useMemo(() => cart.length, [cart])

  const validateCart = useCallback(
    productIds =>
      productIds.reduce(
        (result, productId) => {
          const match = storeData?.products.find(
            product => product.id === productId
          )

          if (!match) {
            // Invalid product (no match found)
            return result
          }

          if (!match.supply) {
            result.removedProductIds.push(match.id)
          } else {
            result.validProductIds.push(match.id)
          }

          return result
        },
        { validProductIds: [], removedProductIds: [] }
      ),
    [storeData]
  )

  /**
   * Parse an existing cart from the users local storage
   * and apply it to the state
   */
  const parseExistingCart = useCallback(() => {
    try {
      const existingCart =
        JSON.parse(localStorage.getItem(LOCAL_STORAGE.userCart)) || []

      const { removedProductIds, validProductIds } = validateCart(existingCart)

      localStorage.setItem(
        LOCAL_STORAGE.userCart,
        JSON.stringify(validProductIds)
      )

      setCart(validProductIds)
      setLostSupplyCart(removedProductIds)
    } catch (error) {
      // Handle errors if needed
    }
  }, [setCart, setLostSupplyCart, storeData, validateCart])

  /**
   * Memo: Keep a tally of the cart total price
   */
  const cartTotalPrice = useMemo(() => {
    if (!storeData) {
      return 0
    }

    return cart.reduce((total, productId) => {
      const match = storeData?.products.find(
        product => product.id === productId
      )

      return total + match?.price || 0
    }, 0)
  }, [cart, storeData])

  /**
   * Add a product to the cart by its ID
   */
  const addToCart = useCallback(
    productId => {
      const match = storeData?.products.find(
        product => product.id === productId
      )

      if (!match || !match?.supply) {
        return
      }

      if (lastCartCount.current === constants.MAX_CART) {
        showModal({
          id: 'marketplace:cart:full'
        })

        return
      }

      setCart(lastCart => {
        const updatedCart = [...new Set([...lastCart, productId])]

        try {
          const stringifiedCart = JSON.stringify(updatedCart)

          localStorage.setItem(LOCAL_STORAGE.userCart, stringifiedCart)
        } catch (e) {
          debug.log('Failed to update cart local storage', e)
        }

        return updatedCart
      })
    },
    [storeData]
  )

  /**
   * Removes a product from the cart by its ID
   */
  const removeFromCart = useCallback(
    productId => {
      setCart(lastCart => {
        const updatedCart = lastCart.filter(id => id !== productId)

        try {
          const stringifiedCart = JSON.stringify(updatedCart)

          localStorage.setItem(LOCAL_STORAGE.userCart, stringifiedCart)
        } catch (e) {
          debug.log('Failed to update cart local storage', e)
        }

        return updatedCart
      })
    },
    [storeData]
  )

  /**
   * Checkout with cart
   */
  const checkoutCart = useCallback(async () => {
    if (!isSignedIn) {
      const res = await showModal({
        id: 'app:sign_in',
        props: {
          completeAction: 'checkout',
          completeLabel: 'Checkout'
        }
      })

      if (res !== 'checkout') {
        // Wasn't signed in OR closed without proceeding to
        // checkout
        return
      }
    }

    const checkoutData = {
      itemIds: cart,
      itemQuantities: cart.map(() => 1),
      total: cartTotalPrice
    }

    showModal({
      id: 'marketplace:checkout',
      props: {
        checkoutData
      }
    })
  }, [cart, cartTotalPrice, storeData, isSignedIn])

  /**
   * Checkout with single product
   */
  const checkoutSingle = useCallback(
    async productId => {
      const match = storeData?.products.find(
        product => product.id === productId
      )

      if (!match || !match?.supply) {
        return
      }

      if (!isSignedIn) {
        const res = await showModal({
          id: 'app:sign_in',
          props: {
            completeAction: 'checkout',
            completeLabel: 'Checkout'
          }
        })

        if (res !== 'checkout') {
          // Wasn't signed in OR closed without proceeding to
          // checkout
          return
        }
      }

      const checkoutData = {
        itemIds: [match.id],
        itemQuantities: [1],
        total: match.price
      }

      showModal({
        id: 'marketplace:checkout',
        props: {
          isSingle: true,
          product: match,
          checkoutData
        }
      })
    },
    [cart, storeData, isSignedIn]
  )

  /**
   * Clears the users cart
   */
  const clearCart = useCallback(() => {
    localStorage.removeItem(LOCAL_STORAGE.userCart)
    setCart([])
    setLostSupplyCart([])
  }, [])

  const dismissCartLostSupply = useCallback(() => {
    setLostSupplyCart([])
  }, [])

  /**
   * Effect: When the cart changes, update the count ref
   */
  useEffect(() => {
    lastCartCount.current = cart.length
  }, [cart])

  /**
   * Effect: On
   */
  useEffect(() => {
    if (!storeData || isCartReady) {
      return
    }

    parseExistingCart()
    setIsCartReady(true)
  }, [storeData, isCartReady, parseExistingCart])

  /**
   * Effect: When the storeData changes, check the cart
   * for any changes to items (such as lost supply) and
   * remove them from the cart, flagging the removed items
   * in the process
   */
  useEffect(() => {
    if (!storeData || !cart.length) {
      return
    }

    const { removedProductIds, validProductIds } = validateCart(cart)

    if (removedProductIds.length) {
      // Products were removed
      setLostSupplyCart(removedProductIds)
      setCart(validProductIds)
    }
  }, [storeData, cart, validateCart])

  /**
   * Memo: the context object
   */
  const ctx = useMemo(
    () => ({
      addToCart,
      cart,
      cartCount,
      cartTotalPrice,
      checkoutCart,
      checkoutSingle,
      clearCart,
      dismissCartLostSupply,
      isCartReady,
      lostSupplyCart,
      removeFromCart
    }),
    [
      addToCart,
      cart,
      cartCount,
      cartTotalPrice,
      checkoutCart,
      checkoutSingle,
      dismissCartLostSupply,
      isCartReady,
      lostSupplyCart,
      removeFromCart
    ]
  )

  return ctx
}

// export
export default useCart
